import { Outlet, useLocation, useNavigate } from 'react-router'

import { Tabs, TabsList, TabsTrigger } from '@/components/elements/tabs'
import { Layout } from '@/components/Layout'

import { useCoursesScope } from './useCoursesScope'

/**
 * Single nav entry that groups the related course surfaces into tabs:
 *
 *   Admin    — /admin/courses                          → Courses · Course Modules · Course Assets
 *   Accounts — /accounts/:entityId/instructors/courses → Custom Courses · Course Modules · Course Assets
 *   Org      — /organizations/:organizationId/courses  → Custom Courses · Course Modules · Course Assets
 *
 * Same three tabs in every scope; facility + org rename the certificate tab to
 * "Custom Courses" and the underlying queries filter to that scope's own rows.
 * The facility hub lives under the Instructor nav group (RSCX-196: edit rights
 * follow teach rights). The org hub (RSCX-320) gives an organization the exact
 * same authoring experience, scoped to the org and shared down to its
 * facilities.
 *
 * Detail/editor routes (e.g. .../course-modules/:id) are still declared
 * under the hub path, but the hub renders just <Outlet /> for them so the
 * editor keeps its own Layout and isn't wrapped by the tab chrome.
 *
 * Matches the AccountManagement tabs pattern already used in the app.
 */
export const CoursesHub = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isCustom, isOrg, hubBase, rootCrumb, certLabel } = useCoursesScope()

  // basePath must match the URL exactly or the subPath slice below falls off
  // the URL and we mistake the active tab for a detail route (which hides the
  // Layout chrome).
  const basePath = hubBase

  // Same tab set in every scope — Certificate + Course + CourseAsset all
  // support facility and org ownership, so both get the full experience. The
  // Certificates tab is labelled "Custom Courses" in custom scopes because the
  // view filters to that scope's own certificates only.
  const tabs = [
    { value: 'courses', label: certLabel },
    { value: 'course-modules', label: 'Course Modules' },
    { value: 'course-assets', label: 'Course Assets' },
  ]

  // Detail routes look like `<base>/<tab>/<id>` — strip the hub prefix, then
  // anything with a second segment is a nested editor that should render
  // without the tabs chrome.
  const subPath = location.pathname.slice(basePath.length).replace(/^\/+/, '')
  const [activeTab = tabs[0].value, ...rest] = subPath.split('/').filter(Boolean)
  const isDetailRoute = rest.length > 0

  if (isDetailRoute) {
    return <Outlet />
  }

  const handleTabChange = (value) => navigate(`${basePath}/${value}`)

  return (
    <Layout breadcrumbs={[rootCrumb, certLabel]}>
      <Layout.Heading title={certLabel} />

      {isCustom && (
        <p className="text-muted-foreground mt-1 max-w-3xl text-sm">
          Upload <span className="font-medium">Course Assets</span> (videos, PDFs, presentations),
          group them into <span className="font-medium">Course Modules</span> (the lessons your
          members complete), then bundle modules into a{' '}
          <span className="font-medium">Custom Course</span> they earn certification for. Assets
          and modules can be reused across multiple courses.
          {isOrg && (
            <>
              {' '}
              Everything you build here is{' '}
              <span className="font-medium">shared read-only with every facility</span> in your
              organization.
            </>
          )}
        </p>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
        <TabsList>
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-4">
          <Outlet />
        </div>
      </Tabs>
    </Layout>
  )
}
